import i18n from "localization/i18n";
import React from "react";
import { ToastContainer, toast, ToastOptions, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Snackbar.css";

const InfoOutlinedSvg = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const snackStyle = {
  fontFamily:
    "'Noto Kufi Arabic', Alexandria, 'Segoe UI', Arial, Roboto, 'Helvetica Neue', sans-serif",
};

export type SnackbarVariant = "solid" | "subtle";

/**
 * DOM contract: every toast body carries `data-testid="toast"` plus
 * `data-toast-type="success" | "error" | "info" | "warning"`, and the message
 * text is the element's text content. Assert with
 * `getByTestId("toast").filter({ has: page.locator('[data-toast-type=error]') })`
 * or scope straight to `[data-testid="toast"][data-toast-type="error"]`, so
 * parallel toasts of different severities never cross-match.
 */
const toastBodyProps = (type: string) => ({
  "data-testid": "toast",
  "data-toast-type": type,
});

export const Snackbar = (
  message: string,
  type: "success" | "error" | "info" | "warning",
  duration?: number,
  variant: SnackbarVariant = "solid",
) => {
  const options: ToastOptions = {
    position: "top-center",
    autoClose: duration ?? 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: variant === "subtle" ? "light" : "colored",
    type: type as ToastOptions["type"],
    transition: Slide,
    rtl: i18n.language == "ar",
    toastId: `${variant}:${type}:${message}`,
    style: snackStyle,
    icon: type === "info" || type === "error" ? <InfoOutlinedSvg /> : undefined,
  };

  const toastId = `${variant}:${type}:${message}`;
  if (!toast.isActive(toastId)) {
    if (message.includes("<")) {
      toast(
        () => (
          <div
            {...toastBodyProps(type)}
            dangerouslySetInnerHTML={{ __html: message }}
            style={{ whiteSpace: "pre-line" }}
          />
        ),
        {
          ...options,
          toastId,
        },
      );
    } else {
      toast(<span {...toastBodyProps(type)}>{message}</span>, options);
    }
  }
};
const SnackbarApp: React.FC = () => (
  <ToastContainer limit={3} bodyStyle={snackStyle} />
);

export default SnackbarApp;
